import { useQuery } from '@tanstack/react-query';
import { Globe, ShieldCheck } from 'lucide-react';
import { PageHeader } from '../components/AppShell';
import { Topbar } from '../components/Topbar';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Spinner } from '../components/ui/spinner';
import { qk } from '../lib/api/keys';
import { useAuthStore } from '../lib/auth/store';
import { fetchLocales } from '../lib/locales';

export default function Settings(): JSX.Element {
  const localesQ = useQuery({ queryKey: qk.locales(), queryFn: fetchLocales });
  const me = useAuthStore((s) => s.user);

  return (
    <>
      <Topbar title="Settings" />
      <PageHeader title="Settings" subtitle="System configuration and account info" />
      <div className="grid grid-cols-1 gap-6 px-6 pb-10 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Locales</CardTitle>
            <CardDescription>
              Configured at the API via <code>LOCALES</code> and <code>LOCALE_LABELS</code> env
              vars.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {localesQ.isLoading ? (
              <Spinner label="Loading…" />
            ) : localesQ.data ? (
              <ul className="grid grid-cols-2 gap-2">
                {localesQ.data.items.map((l) => (
                  <li
                    key={l.code}
                    className="flex items-center justify-between rounded-md border border-line bg-surface-muted px-3 py-2 text-sm"
                  >
                    <span className="font-mono uppercase">{l.code}</span>
                    <span className="text-ink-muted">{l.label}</span>
                    {l.code === localesQ.data!.default ? (
                      <Badge variant="brand" className="ml-2">
                        default
                      </Badge>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-ink-muted">Could not load locales.</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Your account</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Field label="ID" value={me?.id ?? '—'} mono />
            <Field label="Email" value={me?.email ?? '—'} />
            <Field label="Name" value={me?.name ?? '—'} />
            <Field
              label="Role"
              value={me?.role ?? '—'}
              badge={<ShieldCheck className="h-3 w-3" />}
              uppercase
            />
            <Field label="Joined" value={me?.createdAt ?? '—'} />
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Endpoints reference</CardTitle>
            <CardDescription>Where things live in the API.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <Endpoint
              method="POST"
              path="/admin/notify"
              note="Manual breaking-news broadcast (delayed by BREAKING_NEWS_DELAY_MINUTES)"
            />
            <Endpoint method="GET" path="/admin/users" note="List + filter users" />
            <Endpoint
              method="POST"
              path="/admin/users/:id/role"
              note="Promote/demote (server refuses self-demote)"
            />
            <Endpoint method="GET" path="/admin/channels" note="Channels list (admin)" />
            <Endpoint
              method="GET"
              path="/admin/programmes/:slug"
              note="Programme detail with translations + slots"
            />
            <Endpoint method="GET" path="/admin/events" note="All events (incl. drafts)" />
            <Endpoint
              method="GET"
              path="/schedule/this-week"
              note="Public weekly schedule (used by the calendar)"
            />
            <Endpoint
              method="GET"
              path="/channels/:slug/now-playing"
              note="Public now-playing + Shoutcast stream stats"
            />
            <Endpoint
              method="GET"
              path="/programmes/:slug/feed"
              note="iTunes-compatible podcast RSS"
            />
            <p className="flex items-center gap-2 pt-3 text-xs text-ink-subtle">
              <Globe className="h-3 w-3" /> All endpoints honour <code>?locale=</code>; admin writes
              require an admin JWT.
            </p>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function Field({
  label,
  value,
  mono,
  uppercase,
  badge,
}: {
  label: string;
  value: string;
  mono?: boolean;
  uppercase?: boolean;
  badge?: React.ReactNode;
}): JSX.Element {
  return (
    <div className="grid grid-cols-[120px_1fr] items-baseline gap-2">
      <span className="text-xs uppercase tracking-wide text-ink-subtle">{label}</span>
      <span
        className={`flex items-center gap-1 text-ink${mono ? ' font-mono text-xs' : ''}${uppercase ? ' uppercase' : ''}`}
      >
        {badge}
        {value}
      </span>
    </div>
  );
}

function Endpoint({
  method,
  path,
  note,
}: {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  path: string;
  note?: string;
}): JSX.Element {
  const color =
    method === 'GET'
      ? 'bg-info/10 text-info'
      : method === 'POST'
        ? 'bg-success/10 text-success'
        : method === 'DELETE'
          ? 'bg-danger/10 text-danger'
          : 'bg-warning/10 text-warning';
  return (
    <div className="grid grid-cols-[60px_220px_1fr] items-baseline gap-3 rounded-md border border-line px-3 py-2">
      <span
        className={`inline-flex w-fit items-center justify-center rounded px-2 py-0.5 text-[10px] font-bold ${color}`}
      >
        {method}
      </span>
      <code className="font-mono text-xs text-ink">{path}</code>
      <span className="text-xs text-ink-muted">{note}</span>
    </div>
  );
}
